import type { ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { readDemoRun } from "@/lib/featherDemoRun";
import { FEATHER_VOUCHER_PATH, featherLiveCheckpointPath } from "@/lib/featherIntegration";

/**
 * Brings an OAuth sign-in that started inside a live Feather demo run back
 * under the preview bar.
 *
 * The backend always returns a sign-in to `/verify/:token` (applicant) or
 * `/voucher-flow?token=` (voucher) on the org's host; it has no way to name
 * another path. During a demo run that drops the preview bar after every
 * sign-in. This wraps those two route elements in builds that carry the demo
 * (never production) and, ONLY when this tab started a demo run, replaces the
 * URL with the /feather equivalent, query string kept, so `?oauth=` and
 * `?status=` reach the same page component.
 *
 * Every real applicant and voucher: this tab has no run, so the child renders
 * unchanged in the same pass. The redirect has to happen INSTEAD of mounting
 * the child, not beside it: the applicant page reads and clears its step stash
 * (`dv_oauth_return_step`) on mount, and a later redirect would lose it.
 *
 * - Applicant: only for this run's own token.
 * - Voucher: only on an OAuth return (`?oauth=`). No voucher token is stored
 *   anywhere; the voucher page renders the same either way. The run is per
 *   tab, so this fires only when the voucher link was opened in the demo tab.
 *   A link clicked in an email opens a new tab with no run, and its sign-in
 *   comes back without the preview bar.
 */
export function FeatherDemoReturn({ kind, children }: { kind: "applicant" | "voucher"; children: ReactNode }) {
  const { token } = useParams();
  const { search } = useLocation();
  const run = readDemoRun();
  if (!run) return <>{children}</>;

  if (kind === "applicant" && token && token === run.token) {
    return <Navigate to={`${featherLiveCheckpointPath(token)}${search}`} replace />;
  }
  if (kind === "voucher" && new URLSearchParams(search).has("oauth")) {
    return <Navigate to={`${FEATHER_VOUCHER_PATH}${search}`} replace />;
  }
  return <>{children}</>;
}
